import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Award, Check, Sparkles } from "lucide-react";
import Seo from "../components/Seo";
import { MaskLine, Marquee, Reveal } from "../components/Motion";
import CtaBand from "../components/CtaBand";
import { useLang } from "../lib/lang";
import { IMAGES } from "../lib/content";

const bentoIcons = [Award, Award, Sparkles, ClipboardIcon, Sparkles, HeartIcon];
function ClipboardIcon(props) { return <Sparkles {...props} />; }
function HeartIcon(props) { return <Sparkles {...props} />; }

export default function Home() {
  const { t } = useLang();
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 70]);

  return (
    <div>
      <Seo title={t.meta.home.title} description={t.meta.home.desc} />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div aria-hidden="true" className="pointer-events-none absolute -left-40 top-20 h-96 w-96 rounded-full bg-[#FFEDD5] blur-3xl" />
        <div className="mx-auto grid max-w-6xl gap-12 px-5 pb-20 pt-10 sm:pt-14 lg:grid-cols-12 lg:items-center lg:gap-10 lg:pb-28">
          <div className="lg:col-span-7">
            <Reveal delay={0.05} y={12}>
              <p className="eyebrow">{t.hero.eyebrow}</p>
            </Reveal>
            <h1 className="mt-5 font-display text-5xl leading-[1.08] text-ink sm:text-6xl lg:text-7xl">
              <MaskLine delay={0.15}>{t.hero.line1}</MaskLine>
              <MaskLine delay={0.32}>
                <span className="italic text-saffron brush-underline inline-block">{t.hero.line2}</span>
              </MaskLine>
            </h1>
            <Reveal delay={0.55} y={18}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-[#475569] sm:text-lg">
                {t.hero.sub}
              </p>
            </Reveal>
            <Reveal delay={0.7} y={14}>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link to="/contact" data-testid="hero-cta-primary" className="btn-primary">
                  {t.hero.ctaPrimary}
                  <ArrowRight size={17} />
                </Link>
                <Link to="/about" data-testid="hero-cta-secondary" className="btn-outline">
                  {t.hero.ctaSecondary}
                </Link>
              </div>
              <ul className="mt-8 flex flex-wrap gap-2.5">
                {t.hero.badges.map((b, i) => (
                  <li key={i} className="inline-flex items-center gap-1.5 rounded-full border border-[#E5DECF] bg-cream px-4 py-1.5 text-xs font-semibold text-ink">
                    <Check size={13} className="text-saffron" />
                    {b}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div style={{ y: heroY }} className="relative">
              <Reveal delay={0.3}>
                <div className="relative rotate-2 overflow-hidden rounded-[2rem] border-4 border-white shadow-[0_30px_60px_-25px_rgba(30,27,75,0.4)]">
                  <img
                    src={IMAGES.heroTeacher}
                    alt={t.hero.photoAlt}
                    loading="eager"
                    className="aspect-[4/5] w-full object-cover"
                    data-testid="hero-photo"
                  />
                </div>
              </Reveal>
              <div className="float-slow absolute -left-4 top-6 rounded-2xl border border-[#E5DECF] bg-[#FDFBF7]/95 px-5 py-3.5 shadow-xl backdrop-blur" data-testid="hero-badge-years">
                <span className="block font-display text-2xl text-saffron">25+</span>
                <span className="block text-xs font-semibold text-ink">{t.hero.badges[0]}</span>
              </div>
              <div className="absolute -bottom-5 -right-2 max-w-[220px] rounded-2xl border border-[#E5DECF] bg-ink px-5 py-4 text-cream shadow-xl sm:-right-6" data-testid="hero-note-card">
                <p className="text-[13px] leading-snug">{t.hero.floatingNote}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee items={t.marquee} />

      {/* Credentials bento */}
      <section className="mx-auto max-w-6xl px-5 py-20 sm:py-24" data-testid="home-bento">
        <Reveal>
          <p className="eyebrow">{t.bento.eyebrow}</p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl text-ink sm:text-4xl lg:text-5xl">
            {t.bento.title} <span className="text-saffron">{t.bento.titleAccent}</span>
          </h2>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-[#475569]">{t.bento.sub}</p>
        </Reveal>
        <div className="mt-12 grid gap-5 md:grid-cols-6">
          {t.bento.cards.map((card, i) => {
            const Icon = bentoIcons[i];
            const span = i === 0 ? "md:col-span-6 lg:col-span-3" : i < 3 ? "md:col-span-3" : "md:col-span-2";
            return (
              <Reveal key={i} delay={0.07 * i} className={span}>
                <article
                  data-testid={`bento-card-${i}`}
                  className={`card-warm group h-full p-7 hover:-translate-y-1 hover:shadow-[0_24px_50px_-24px_rgba(30,27,75,0.35)] ${i === 0 ? "bg-ink !border-ink text-cream" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    {card.stat ? (
                      <span className="font-display text-6xl text-saffron">{card.stat}</span>
                    ) : (
                      <span className={`flex h-11 w-11 items-center justify-center rounded-full ${i === 0 ? "bg-cream/10 text-marigold" : "bg-[#FFEDD5] text-saffron"}`}>
                        <Icon size={20} />
                      </span>
                    )}
                  </div>
                  <h3 className={`mt-5 font-display text-xl ${i === 0 ? "text-cream" : "text-ink"}`}>{card.label}</h3>
                  <p className={`mt-2.5 text-sm leading-relaxed ${i === 0 ? "text-cream/75" : "text-[#475569]"}`}>{card.text}</p>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Who it's for */}
      <section className="bg-cream py-20 sm:py-24" data-testid="home-programs">
        <div className="mx-auto max-w-6xl px-5">
          <Reveal>
            <p className="eyebrow">{t.programs.eyebrow}</p>
            <h2 className="mt-4 max-w-2xl font-display text-3xl text-ink sm:text-4xl lg:text-5xl">{t.programs.title}</h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-[#475569]">{t.programs.sub}</p>
          </Reveal>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {t.programs.cards.map((p, i) => (
              <Reveal key={i} delay={0.08 * i}>
                <article className="card-warm flex h-full flex-col p-8 hover:-translate-y-1 hover:shadow-[0_24px_50px_-24px_rgba(30,27,75,0.35)]" data-testid={`program-card-${i}`}>
                  <span className="inline-flex w-fit rounded-full bg-[#FFEDD5] px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-[#9A3412]">{p.tag}</span>
                  <h3 className="mt-4 font-display text-2xl text-ink">{p.title}</h3>
                  <ul className="mt-5 space-y-3">
                    {p.items.map((it, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm leading-relaxed text-[#475569]">
                        <Check size={16} className="mt-0.5 shrink-0 text-saffron" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-10 grid gap-6 overflow-hidden rounded-[2.5rem] lg:grid-cols-2">
              <img src={IMAGES.classroomKids} alt={t.programs.imageAlt} loading="lazy" className="h-64 w-full rounded-[2rem] object-cover lg:h-full" data-testid="programs-photo" />
              <img src={IMAGES.illOnline} alt="" loading="lazy" className="h-64 w-full rounded-[2rem] object-cover object-top lg:h-full" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* How enquiring works */}
      <section className="mx-auto max-w-6xl px-5 py-20 sm:py-24" data-testid="home-steps">
        <Reveal>
          <p className="eyebrow">{t.steps.eyebrow}</p>
          <h2 className="mt-4 font-display text-3xl text-ink sm:text-4xl lg:text-5xl">{t.steps.title}</h2>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-[#475569]">{t.steps.sub}</p>
        </Reveal>
        <ol className="mt-12 grid gap-6 md:grid-cols-3">
          {t.steps.items.map((s, i) => (
            <Reveal key={i} delay={0.08 * i}>
              <li className="card-warm relative h-full p-8" data-testid={`step-card-${i}`}>
                <span className="font-display text-5xl text-[#E5DECF]">0{i + 1}</span>
                <h3 className="mt-4 font-display text-xl text-ink">{s.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-[#475569]">{s.text}</p>
              </li>
            </Reveal>
          ))}
        </ol>
        <Reveal delay={0.2}>
          <div className="mt-10 text-center">
            <Link to="/contact" data-testid="steps-cta" className="btn-saffron">
              {t.steps.cta}
              <ArrowRight size={17} />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* FAQ teaser */}
      <section className="bg-cream py-20 sm:py-24" data-testid="home-faq-teaser">
        <div className="mx-auto grid max-w-6xl gap-10 px-5 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <p className="eyebrow">{t.faqTeaser.eyebrow}</p>
            <h2 className="mt-4 font-display text-3xl text-ink sm:text-4xl">{t.faqTeaser.title}</h2>
            <ul className="mt-8 space-y-5">
              {[t.faqs.items[1], t.faqs.items[3], t.faqs.items[4]].map((f, i) => (
                <li key={i} className="border-b border-[#E5DECF] pb-5" data-testid={`faq-teaser-item-${i}`}>
                  <p className="font-semibold text-ink">{f.q}</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-[#475569]">{f.a}</p>
                </li>
              ))}
            </ul>
            <Link to="/faqs" data-testid="faq-teaser-link" className="mt-7 inline-flex items-center gap-2 font-semibold text-saffron underline decoration-2 underline-offset-4 hover:text-ink">
              {t.faqTeaser.cta}
              <ArrowRight size={16} />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <img
              src={IMAGES.boyBook}
              alt={t.about.photoAlt}
              loading="lazy"
              className="h-80 w-full rounded-[2.5rem] object-cover shadow-[0_30px_60px_-25px_rgba(30,27,75,0.35)] lg:h-[480px]"
              data-testid="faq-teaser-photo"
            />
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
